<template>
  <v-col class="pa-0">
    <div
      v-if="!this.isBelowSmallestBreakpoint && !$rttI18n.$isWidget"
      :style="
        $rttI18n.isGlsItSelected || $rttI18n.isGlsPlSelected
          ? { visibility: 'hidden' }
          : null
      "
      class="py-4"
    >
      <h2 class="font-weight-bold">{{ $t('overview.title') }}</h2>
    </div>

    <EtaInformationBanner />
    <v-card
      class="card_big v-col-12 overview mb-6"
      data-testid="overview-card"
      variant="flat"
    >
      <div
        v-if="
          this.getTrackingState === TRACKING_DELIVERY_STATE_CLOSE ||
          this.getTrackingState === TRACKING_DELIVERY_STATE_FAR ||
          this.getTrackingState === TRACKING_DELIVERY_STATE_NEAR ||
          (this.isJobStatusShadowed && this.getTimeframe)
        "
      >
        <div
          v-if="this.getTrackingState === TRACKING_DELIVERY_STATE_FAR"
          class="text-footnote mb-4"
        >
          <span>
            {{
              $t('overview.details.stateFar', {
                stops: this.trackingData.trackingStateThresholdMax
              })
            }}
          </span>
        </div>
        <div
          v-if="
            this.getTrackingState === TRACKING_DELIVERY_STATE_CLOSE ||
            this.getTrackingState === TRACKING_DELIVERY_STATE_NEAR ||
            this.isJobStatusShadowed
          "
          class="text-footnote mb-4"
        >
          <span v-if="this.getTimeframe">{{
            $rttI18n.tenantSpecificWithFallback(
              !this.isServiceTypePickup
                ? 'overview.details.deliveryTimestamp'
                : 'overview.details.pickupTimestamp'
            )
          }}</span>
          <span v-else>{{
            $t(
              !this.isServiceTypePickup
                ? 'overview.details.unclear.description'
                : 'overview.details.unclearPickup.description'
            )
          }}</span>
        </div>
        <h3 v-if="this.getTimeframe" class="text-h3">
          {{ this.timeFrameDesc }}
        </h3>
        <v-btn
          v-if="this.trackingData.timeFrameEligible"
          :block="true"
          :disabled="hasCustomizedDeliveryPeriod()"
          class="text-body-1 my-4"
          color="anchor"
          data-testid="btn-customizeDeliveryPeriod"
          variant="outlined"
          v-on:click="dialogHandler(dialogs.DELIVERY_PERIOD)"
        >
          <span>{{ $t('overview.details.customizeDeliveryPeriod') }}</span>
          <img
            :alt="$t('overview.details.customizeDeliveryPeriod')"
            class="deliveryPeriodIcon"
            src="/icon_delivery_period.svg"
          />
        </v-btn>
        <hr v-else class="my-5" aria-hidden="true" />
        <LastUpdatedComponent
          :updateTimestamp="this.trackingData.updateTimestamp"
        />
      </div>

      <div
        v-if="this.getTrackingState === this.TRACKING_DELIVERY_STATE_LAST"
        class="d-flex flex-column-reverse"
      >
        <LastUpdatedComponent
          :updateTimestamp="this.trackingData.updateTimestamp"
        />
        <hr class="my-5" aria-hidden="true" />
        <h3 class="text-h6">
          {{ overviewHeader }}
        </h3>
      </div>

      <div
        v-if="
          this.isJobStatusUnclear ||
          this.isJobStatusBlocked ||
          (this.isJobStatusShadowed && !this.getTimeframe)
        "
        class="d-flex flex-column-reverse"
      >
        <LastUpdatedComponent
          :updateTimestamp="this.trackingData.updateTimestamp"
        />
        <hr class="my-5" aria-hidden="true" />
        <h3 class="text-h6">
          {{ unclearHeader }}
        </h3>
      </div>

      <OverviewDeliveredComponent :postcode="postcode" />

      <OverviewPickedUpComponent
        v-if="this.isJobStatusPickedUp"
        :postcode="postcode"
      />

      <div v-if="this.isJobStatusNotDelivered">
        <img
          :alt="$t('overview.details.notDelivered.header')"
          :src="publicPath(`/themes/${this.themeId}/icon_delivery_refused.svg`)"
          style="width: 60px"
        />
        <p :class="['header', $rttI18n.isGlsPlSelected ? 'gls-pl' : '']">
          <span class="font-weight-bold">{{
            $t('overview.details.notDelivered.header')
          }}</span>
        </p>
        <div>
          <hr class="my-5" aria-hidden="true" />
          <p v-if="$rttI18n.isRoyalMailSelected" class="font-weight-bold mb-0">
            {{
              $rttI18n.tenantSpecificWithFallback(
                'overview.details.goBackToDetails'
              )
            }}
          </p>
          <p v-else>
            <span>{{
              $t(
                `overview.details.notDelivered.reasons.${this.trackingData.status.detail}`
              )
            }}</span>
          </p>
        </div>
      </div>

      <div v-if="this.isJobStatusNotPickedUp">
        <img
          :alt="$t('overview.details.notPickedUp.header')"
          :src="publicPath(`/themes/${this.themeId}/icon_delivery_refused.svg`)"
          style="width: 60px"
        />
        <p class="header">
          <span class="font-weight-bold">{{
            $t('overview.details.notPickedUp.header')
          }}</span>
        </p>
        <hr class="my-5" aria-hidden="true" />
        <div
          :class="[
            $rttI18n.isGlsItSelected ? 'd-flex flex-column-reverse' : ''
          ]"
        >
          <span>{{
            $t(
              `overview.details.notPickedUp.reasons.${this.trackingData.status.detail}`
            )
          }}</span>
        </div>
      </div>

      <div v-if="this.isJobStatusUnknown">
        <h3 class="text-h6">
          {{ $t('overview.details.unclear.header') }}
        </h3>
        <hr class="my-5" aria-hidden="true" />
        <LastUpdatedComponent
          :updateTimestamp="this.trackingData.updateTimestamp"
        />
      </div>
      <div
        v-if="
          (this.isJobStatusDelivered || $rttI18n.isGlsEsSelected) &&
          getTrackingNote()
        "
      >
        <hr class="my-5" aria-hidden="true" />
        <span
          class="font-weight-bold tracking-note"
          v-html="sanitize(getTrackingNote(), { ADD_ATTR: ['target'] })"
        />
      </div>
    </v-card>
    <div
      v-if="
        this.consigneeAddress &&
        this.consigneeAddress.postcode &&
        !this.isJobStatusDelivered &&
        !this.isJobStatusNotDelivered &&
        !this.isJobStatusUnknown
      "
    >
      <div
        v-if="
          this.isBelowSmallestBreakpoint &&
          showCustomizationButtons &&
          this.trackingData.supportedCustomizedDeliveryTypes.length > 0 &&
          !(
            configGetter(this.$rttI18n.tenantId, this.themeId)
              .disableDepositPermission &&
            configGetter(this.$rttI18n.tenantId, this.themeId)
              .disableDeliverToNeighbour
          )
        "
        class="my-4"
      >
        <v-btn
          :block="true"
          class="text-body-1"
          color="info"
          data-testid="btn-customizeDelivery"
          variant="flat"
          v-on:click="dialogHandler(dialogs.CUSTOMIZE)"
        >
          {{ $t('overview.details.customizeDelivery') }}
        </v-btn>
      </div>
      <div v-if="!isBelowSmallestBreakpoint">
        <v-btn
          v-if="
            this.trackingData.supportedCustomizedDeliveryTypes.includes(
              DEPOSIT_PERMISSION
            ) &&
            !configGetter(this.$rttI18n.tenantId, this.themeId)
              .disableDepositPermission &&
            showCustomizationButtons
          "
          :block="true"
          class="text-body-1 my-4"
          color="info"
          variant="flat"
          v-on:click="dialogHandler(dialogs.DROP_OFF)"
        >
          {{ $t('customization.dropOffPermission.header') }}
        </v-btn>
        <v-btn
          v-if="
            this.trackingData.supportedCustomizedDeliveryTypes.includes(
              DELIVER_TO_NEIGHBOUR
            ) &&
            !configGetter(this.$rttI18n.tenantId, this.themeId)
              .disableDeliverToNeighbour &&
            showCustomizationButtons
          "
          :block="true"
          class="text-body-1 my-4"
          color="info"
          variant="flat"
          v-on:click="dialogHandler(dialogs.NEIGHBOUR)"
        >
          {{ $t('customization.neighbour.header') }}
        </v-btn>

        <v-btn
          v-if="
            this.trackingData.supportedCustomizedDeliveryTypes.includes(
              DELIVER_TO_PARCELSHOP
            ) &&
            !configGetter(this.$rttI18n.tenantId, this.themeId)
              .disableDeliverToNeighbour &&
            showCustomizationButtons
          "
          :block="true"
          class="text-body-1 my-4"
          color="info"
          variant="flat"
          v-on:click="dialogHandler(dialogs.DELIVER_TO_PARCELSHOP)"
        >
          {{ $t('customization.deliverToParcelShop.dialog.header') }}
        </v-btn>

        <v-btn
          v-if="
            !$rttI18n.$isWidget &&
            !this.isJobStatusDelivered &&
            this.trackingData.supportedCustomizedDeliveryTypes.includes(
              DELIVERY_NOTE
            ) &&
            showCustomizationButtons
          "
          :block="true"
          class="text-body-1"
          color="info"
          variant="flat"
          v-on:click="dialogHandler(dialogs.DELIVERY_NOTE)"
        >
          {{ $t('customization.deliveryHints.header') }}
        </v-btn>
      </div>
    </div>
  </v-col>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import moment from 'moment-timezone';
import LastUpdatedComponent from './LastUpdatedComponent';
import OverviewDeliveredComponent from '@/components/OverviewDeliveredComponent';
import {
  DELIVER_TO_NEIGHBOUR,
  DELIVER_TO_PARCELSHOP,
  DELIVERY_NOTE,
  DEPOSIT_PERMISSION,
  NOTIFICATION_TYPE_ELIGIBLE,
  NOTIFICATION_TYPE_SET,
  TRACKING_DELIVERY_STATE_CLOSE,
  TRACKING_DELIVERY_STATE_FAR,
  TRACKING_DELIVERY_STATE_LAST,
  TRACKING_DELIVERY_STATE_NEAR,
  TRACKING_DELIVERY_STATE_UNCLEAR,
  TRACKING_STATE_BLOCKED,
  TRACKING_STATE_DELIVERED,
  TRACKING_STATE_DETAIL_NEIGHBOR,
  TRACKING_STATE_NOT_DELIVERED,
  TRACKING_STATE_NOT_PICKED_UP,
  TRACKING_STATE_SHADOWED,
  TRACKING_STATE_UNKNOWN
} from '@/stores/constants.js';
import OverviewPickedUpComponent from '@/components/OverviewPickedUpComponent';
import { configGetter } from '@/config/configGetter';
import { getUtmSource } from '@/router/queryParam';
import publicPath from '@/util/publicPath';
import EtaInformationBanner from '@/components/EtaInformationBanner.vue';
import dialogs from '@/components/dialogs/dialogs.js';
import { useDialogStore } from '@/stores/dialog.js';
import { useResponsiveBreakpointsStore } from '@/stores/responsiveBreakpoints.js';
import { useDeliveryPeriodStore } from '@/stores/deliveryPeriod.js';
import { useThemeStore } from '@/stores/theme.js';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { useCustomizedDeliveryStore } from '@/stores/customizedDelivery.js';
import DOMPurify from 'dompurify';

export default {
  inheritAttrs: false,
  name: 'OverviewComponent',
  components: {
    EtaInformationBanner,
    LastUpdatedComponent,
    OverviewDeliveredComponent,
    OverviewPickedUpComponent
  },
  data() {
    return {
      postcode: this.$route.params.postcode || '',
      DELIVER_TO_NEIGHBOUR,
      DELIVERY_NOTE,
      DELIVER_TO_PARCELSHOP,
      DEPOSIT_PERMISSION,
      NOTIFICATION_TYPE_ELIGIBLE,
      NOTIFICATION_TYPE_SET,
      TRACKING_STATE_BLOCKED,
      TRACKING_DELIVERY_STATE_CLOSE,
      TRACKING_STATE_DELIVERED,
      TRACKING_STATE_DETAIL_NEIGHBOR,
      TRACKING_DELIVERY_STATE_FAR,
      TRACKING_DELIVERY_STATE_LAST,
      TRACKING_DELIVERY_STATE_NEAR,
      TRACKING_STATE_NOT_DELIVERED,
      TRACKING_STATE_NOT_PICKED_UP,
      TRACKING_DELIVERY_STATE_UNCLEAR,
      TRACKING_STATE_UNKNOWN,
      TRACKING_STATE_SHADOWED,
      dialogs
    };
  },
  computed: {
    ...mapState(useTrackingDataStore, {
      trackingData: 'data',
      getTrackingState: 'getTrackingState',
      getTimeframe: 'getTimeframe',
      isServiceTypePickup: 'isServiceTypePickup',
      isJobStatusShadowed: 'isJobStatusShadowed',
      isJobStatusUnclear: 'isJobStatusUnclear',
      isJobStatusBlocked: 'isJobStatusBlocked',
      isJobStatusNotDelivered: 'isJobStatusNotDelivered',
      isJobStatusPickedUp: 'isJobStatusPickedUp',
      isJobStatusNotPickedUp: 'isJobStatusNotPickedUp',
      isJobStatusUnknown: 'isJobStatusUnknown',
      isJobStatusDelivered: 'isJobStatusDelivered',
      consigneeAddress: 'consigneeAddress'
    }),
    ...mapState(useResponsiveBreakpointsStore, ['isBelowSmallestBreakpoint']),
    ...mapState(useDeliveryPeriodStore, ['deliveryPeriod']),
    ...mapState(useThemeStore, ['themeId']),
    ...mapState(useCustomizedDeliveryStore, ['customizedDelivery']),
    timeFrameDesc() {
      const { from, to } = this.getTimeframe;

      return `${moment(from).format(this.$rttMoment.timeFormat)} - ${moment(
        to
      ).format(this.$rttMoment.timeFormat)}`;
    },
    overviewHeader() {
      return this.$t(
        this.isServiceTypePickup
          ? 'overview.details.pickupLastStop.header'
          : 'overview.details.deliveryLastStop.header'
      );
    },
    unclearHeader() {
      return this.$t(
        !this.isServiceTypePickup
          ? 'overview.details.unclear.header'
          : 'overview.details.unclearPickup.header'
      );
    },
    showCustomizationButtons() {
      return !this.customizedDelivery;
    }
  },
  methods: {
    sanitize: DOMPurify.sanitize,
    ...mapActions(useDialogStore, ['toggleDialog']),
    publicPath,
    configGetter,
    getUtmSource: () => getUtmSource(this.$router),
    dialogHandler(dialog) {
      this.toggleDialog(dialog);
    },
    hasCustomizedDeliveryPeriod() {
      return !!this.deliveryPeriod;
    },
    getTrackingNote() {
      if (
        this.$rttI18n.tenantId &&
        !configGetter(this.$rttI18n.tenantId, this.themeId).disableMoreInfoLink
      ) {
        return this.$rttI18n.tenantSpecificContent('glsTrackingNote', {
          trackingId: this.trackingData.trackingId,
          postcode: this.postcode
        });
      }
      return '';
    }
  },
  watch: {
    '$route.params.postcode'(data) {
      this.postcode = data;
    }
  }
};
</script>

<style scoped>
.overview {
  margin-bottom: 10px;
  text-align: center;
}

.overview p {
  margin-bottom: 0;
  color: #000;
}

.tracking-note {
  font-size: 14px;
}

.overview .header {
  &.gls-pl {
    color: black;
  }
  color: #061ab1;
  font-size: 16px;
}

.overview .v-icon {
  font-size: 36px;
  line-height: 48px;
}

.overview hr {
  border: none;
  border-bottom: 1px solid #e6e8f7;
}

button.secondary {
  color: #061ab1 !important;
  background-color: transparent !important;
  border: 1px solid #061ab1 !important;
}

.deliveryPeriodIcon {
  margin-bottom: 3px;
  margin-left: 3px;
  width: 26px;
  /* filter: brightness(0); */ /* https://codepen.io/sosuke/pen/Pjoqqp */
}
</style>
